import React, { createContext, useState, useEffect } from 'react';
import { getInitialLanguage, SUPPORTED_LANGUAGES, getBrowserLanguage } from '../config/languageConfig';

export const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage());
  const [translations, setTranslations] = useState({});
  const [ready, setReady] = useState(false);
  const [isUsingBrowserDefault, setIsUsingBrowserDefault] = useState(!localStorage.getItem('userLanguage'));

  // Listen for browser language changes when using browser default
  useEffect(() => {
    if (isUsingBrowserDefault) {
      const handleLanguageChange = () => {
        const browserLang = getBrowserLanguage();
        setLanguage(browserLang);
      };

      window.addEventListener('languagechange', handleLanguageChange);
      return () => window.removeEventListener('languagechange', handleLanguageChange);
    }
  }, [isUsingBrowserDefault]);

  useEffect(() => {
    async function loadTranslations() {
      try {
        const module = await import(`../translations/${language}.json`);
        setTranslations(module.default);
        setReady(true);
      } catch (error) {
        console.error(`Failed to load translations for ${language}:`, error);
        if (language !== 'en') {
          const enModule = await import('../translations/en.json');
          setTranslations(enModule.default);
        }
        setReady(true);
      }
    }

    loadTranslations();
  }, [language]);

  const handleSetLanguage = async (newLanguage, useBrowserDefault = false) => {
    if (SUPPORTED_LANGUAGES.includes(newLanguage)) {
      setLanguage(newLanguage);
      setIsUsingBrowserDefault(useBrowserDefault);
    }
  };

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage: handleSetLanguage,
      translations,
      ready,
      isUsingBrowserDefault
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
} 